import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  TrendingUp,
  Languages,
  RefreshCw
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../utils/api'
import useAuthStore from '../stores/authStore'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import QuotaStatus from '../components/QuotaStatus'
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS } from '../utils/constants'

const DashboardPage = () => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [showRecentJobs, setShowRecentJobs] = useState(false)

  // Only fetch jobs when user explicitly requests them
  const { data: jobsData = {}, isLoading: jobsLoading, refetch: refetchJobs } = useQuery({
    queryKey: ['dashboard-recent-jobs'],
    queryFn: async () => {
      console.log('Fetching recent jobs on user request')
      const response = await api.get('/user/jobs/active', {
        params: { hours_back: 2 } // Show active jobs from last 2 hours
      })
      return { jobs: response.data } // Wrap in expected format
    },
    enabled: false, // Don't fetch automatically
    refetchInterval: showRecentJobs ? 30000 : false, // Only poll when section is visible
  })

  // Extract jobs array from the response
  const jobs = jobsData.jobs || []

  // Fetch usage statistics (optional, don't block page if it fails)
  const { data: stats = {}, isLoading: statsLoading } = useQuery({
    queryKey: ['usage-stats'],
    queryFn: async () => {
      try {
        const response = await api.get('/user/usage-stats')
        return response.data
      } catch (error) {
        console.warn('Usage stats not available:', error.message)
        return {} // Return empty stats if endpoint fails
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once if it fails
  })

  const recentJobs = jobs.slice(0, 5)

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-500" />
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />
    }
  }

  const quickStats = [
    {
      nameKey: 'stats.totalTranslations',
      value: stats.totalJobs || 0,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900'
    },
    {
      nameKey: 'stats.charactersTranslated',
      value: (stats.charactersTranslated || 0).toLocaleString(),
      icon: Languages,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900'
    },
    {
      nameKey: 'stats.reviewCount',
      value: stats.reviewCount || 0,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900'
    },
  ]

  console.log('Dashboard render state:', {
    showRecentJobs,
    jobsLoading,
    statsLoading,
    jobsCount: jobs.length,
    userEmail: user?.email
  })

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('dashboard.welcomeBack', { user: user?.email?.split('@')[0] || 'User' })}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('dashboard.todayActivity')}
        </p>
      </div>

      {/* Quota Status */}
      <div className="mb-8">
        <QuotaStatus showDetails={false} className="mb-4" />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/translate" className="flex-1">
            <Button className="w-full h-12 text-base">
              <Plus className="w-5 h-5 mr-2" />
              {t('actions.newTranslation')}
            </Button>
          </Link>
          <Link to="/docs" className="flex-1">
            <Button variant="outline" className="w-full h-12 text-base">
              <FileText className="w-5 h-5 mr-2" />
              {t('actions.documentation')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {quickStats.map((stat) => (
          <Card key={stat.name}>
            <Card.Content className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor} mr-4`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {statsLoading ? t('stats.loading') : stat.value}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(stat.nameKey)}
                </p>
              </div>
            </Card.Content>
          </Card>
        ))}
      </div>

      {/* Recent Jobs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <Card.Header className="flex items-center justify-between">
            <Card.Title>{t('dashboard.recentActiveJobs')}</Card.Title>
            {!showRecentJobs ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowRecentJobs(true)
                  refetchJobs()
                }}
              >
                {t('dashboard.loadRecentJobs')}
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchJobs()}
                  disabled={jobsLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${jobsLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowRecentJobs(false)
                  }}
                >
                  {t('dashboard.hide')}
                </Button>
              </div>
            )}
          </Card.Header>
          <Card.Content>
            {!showRecentJobs ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {t('dashboard.clickLoadJobs')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {t('dashboard.jobsOnlyLoaded')}
                </p>
              </div>
            ) : jobsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : recentJobs.length > 0 ? (
              <div className="space-y-4">
                {recentJobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(job.status)}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {job.filename || 'Untitled'}
                        </p>
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <span>{job.source_lang} → {job.target_langs?.join(', ')}</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${JOB_STATUS_COLORS[job.status]}`}>
                            {t(JOB_STATUS_LABELS[job.status])}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* No download buttons on dashboard - user goes to translation page for downloads */}
                    <div className="text-sm text-gray-500">
                      {new Date(job.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                <Link to="/translate">
                  <Button variant="outline" className="w-full">
                    {t('dashboard.goToTranslationPage')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {t('dashboard.noActiveJobs')}
                </p>
                <Link to="/translate">
                  <Button>
                    {t('dashboard.startFirstTranslation')}
                  </Button>
                </Link>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Usage Overview */}
        <Card>
          <Card.Header>
            <Card.Title>{t('dashboard.usageOverview')}</Card.Title>
          </Card.Header>
          <Card.Content>
            {statsLoading ? (
              <div className="space-y-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('usage.monthlyQuota')}
                    </span>
                    <div className="text-right">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {(stats.charactersUsed || 0).toLocaleString()} / {(stats.monthlyLimit || 100000).toLocaleString()}
                      </span>
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        {t('usage.periodMonth')}
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(100, ((stats.charactersUsed || 0) / (stats.monthlyLimit || 100000)) * 100)}%` 
                      }}
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.jobsThisMonth || 0}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('usage.jobsThisMonth')}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.avgProcessingTime || 0}s
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('usage.avgProcessing')}
                      </p>
                    </div>
                  </div>
                </div>

                <Link to="/payment" className="block">
                  <Button variant="outline" className="w-full">
                    {t('usage.upgradePlan')}
                  </Button>
                </Link>
              </div>
            )}
          </Card.Content>
        </Card>
      </div>
    </div>
  )
}

export default DashboardPage